import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  ContentChild,
  DoCheck,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { ILogger, LoggerService } from '@wz-services';
import { sortBy } from '@wz-utilities/array.utilities';
import { equalsPropertyWithNullCheck } from '@wz-utilities/object.utilities';
import { Subject } from 'rxjs';
import { FormsModels } from '../../../../../models/forms.model';
import { ResourcesService } from '../../../tunnelsante/services/resources/resources.service';

@Component({
  selector: 'wz-select2',
  templateUrl: './select2.component.html',
  styleUrls: ['./select2.component.scss'],
  standalone: false,
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: Select2Component,
    },
  ],
  // tslint:disable-next-line:use-host-property-decorator
  host: {
    '[class.floating]': 'shouldLabelFloat',
    '[id]': 'id',
    '[attr.aria-describedby]': 'describedBy',
  },
})
export class Select2Component
  implements
    OnInit,
    OnChanges,
    DoCheck,
    OnDestroy,
    ControlValueAccessor,
    MatFormFieldControl<any>
{
  static nextId = 0;

  @ContentChild(TemplateRef, { static: true })
  template!: TemplateRef<any>;
  @ViewChild(MatSelect, { static: true })
  matSelectComponent!: MatSelect;

  errorStateMatcher!: ErrorStateMatcher;
  stateChanges = new Subject<void>();
  focused = false;
  errorState = false;
  describedBy = '';
  id: string;
  boundCompareFn!: any;

  @Output()
  public valueChange: EventEmitter<any> = new EventEmitter<any>();

  @Input()
  public compareWith: (o1: any, o2: any) => boolean = (o1: any, o2: any) =>
    o1 === o2;
  @Input()
  public compareWithProperty!: string;

  @Input()
  emptyText!: string;

  @Input()
  orderBy?: 'asc' | 'desc' | null | undefined = null;

  @Input()
  included!: any[];

  @Input()
  excluded!: any[];

  @Input()
  public isLoading!: boolean;
  @Input()
  public panelClass = 'wz-panel';
  @Input()
  items: FormsModels.SelectListItem[] = [];

  public currentItem!: FormsModels.SelectListItem | any;

  @Input()
  public enumItemsKey!: string | any;

  private _value: any = '-1';

  get value(): any {
    return this._value;
  }

  @Input()
  public multiple: boolean = false;

  @Input()
  set value(val: any) {
    if (val !== this._value) {
      this._value = val;
      this.setCurrentItem();
      if (this._value === '-1') {
        this.valueChange.next(null);
        this.propagateChange(null);
      } else {
        this.valueChange.next(this.value);
        this.propagateChange(this.value);
      }

      this.stateChanges.next();
    }
  }

  get controlType() {
    return 'wz-select';
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get empty(): boolean {
    return (
      this._value === '-1' ||
      this._value === '' ||
      this._value === null ||
      this._value === undefined
    );
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  private _placeholder!: string;

  @Input()
  get required() {
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(dis) {
    this._disabled = coerceBooleanProperty(dis);
    this.stateChanges.next();
  }
  private _disabled = false;
  private logger: ILogger;

  constructor(
    loggerService: LoggerService,
    private focusMonitor: FocusMonitor,
    private elRef: ElementRef,
    @Optional() @Self() @Inject(NgControl) public ngControl: NgControl | any,
    private resourcesService: ResourcesService,
  ) {
    this.logger = loggerService.getLogger('Select2');
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    focusMonitor.monitor(elRef.nativeElement, true).subscribe((origin) => {
      this.focused = !!origin && !this.disabled;
      this.stateChanges.next();
    });

    this.id = this.getId();
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  private setCurrentItem() {
    if (this._value != null && this.items) {
      this.currentItem = this.items.find((i) =>
        this.compareFn(i.value, this._value),
      );
    } else {
      this.currentItem = null;
    }
  }

  ngOnInit() {
    this.boundCompareFn = this.compareFn.bind(this);

    if (this.ngControl) {
      this.required = this.ngControl.control.hasError('required');

      this.ngControl.statusChanges.subscribe((_s: any) => {
        this.errorState = this.ngControl.dirty && this.ngControl.invalid;
      });
    }
  }

  ngOnChanges(_simpleChanges: SimpleChanges) {
    if (this.items || this.enumItemsKey || this.emptyText) {
      this.computeItems();
    }
  }

  computeItems() {
    const defaults =
      this.emptyText !== undefined
        ? [{ text: this.emptyText, value: null }]
        : [];

    if (this.enumItemsKey) {
      this.items = [
        ...defaults,
        ...this.resourcesService.getResourceEnumList(
          this.enumItemsKey,
          this.included,
          this.excluded,
        ),
      ];
    } else {
      this.items = [...defaults, ...this.items];
    }

    if (this.orderBy !== null) {
      this.items = sortBy<FormsModels.SelectListItem>(
        this.items,
        (el) => el.text,
        this.orderBy,
      );
    }

    this.setCurrentItem();
  }

  ngDoCheck() {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elRef.nativeElement);
  }

  private compareFn(a: any, b: any): boolean {
    if (this.compareWithProperty) {
      this.logger.log(`CompareWithProperty set : ${this.compareWithProperty}`);
      if (equalsPropertyWithNullCheck(a, b, this.compareWithProperty)) {
        return true;
      } else {
        this.logger.error(
          'Trying to compare with a property missing from objects',
          {
            a,
            b,
            compareProperty: this.compareWithProperty,
          },
        );
      }
    }

    if (this.compareWith) {
      this.logger.log(
        `compareWith (function) set : ${this.compareWithProperty}`,
      );
      return this.compareWith(a, b);
    }

    return a === b;
  }

  getId() {
    return `${this.controlType}-${Select2Component.nextId++}`;
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(_event: MouseEvent) {
    if (this.matSelectComponent && !this.disabled) {
      this.matSelectComponent.focus();
      this.matSelectComponent.open();
    }
  }

  onSelectChange(val: any) {
    this.value = val;
  }

  updateErrorState() {
    this.errorState = this.ngControl.dirty && this.ngControl.invalid;
  }

  /** ControlValueAccessor imp */
  writeValue(newValue: any) {
    this.value = newValue === undefined || newValue === null ? '-1' : newValue;
  }

  propagateChange = (_: any) => {};

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched() {}
}
